import * as React from 'react';
import classnames from 'classnames';
import isArray from 'lodash/isArray';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import DeliverableDetailsForm, { INPUT_ROWS_TEMPLATES } from 'modules/deliverables/components/deliverable-details/DeliverableDetailsForm';
import DeliverableDetailsFormV2 from 'modules/deliverables/components/deliverable-details-v2/DeliverableDetailsForm';
import DeliverableLinksForm from 'modules/deliverables/components/deliverable-edit-links/DeliverableLinksForm';
import DeliverableHeader from 'modules/deliverables/components/header/DeliverableHeader';
import DeliverableHeaderV2 from 'modules/deliverables/components/header-v2/DeliverableHeader';
import DeliverableEditCommonForm
  from 'modules/deliverables/components/deliverable-edit-common/DeliverableEditCommonForm';
import DeliverableEditCommonFormV2
  from 'modules/deliverables/components/deliverable-edit-common-v2/DeliverableEditCommonForm';
import InfoNote from 'components/info-note/InfoNote';
import PageSection from 'components/page/page-section/PageSection';
import Tooltip from 'components/Tooltip';
import { withScrollTo } from 'components/ScrollToHOC';

import { __t, applyTranslations, t } from 'utils/language';
import {
  DEFAULT_DELIVERABLE_TYPE_IN_COMPOSER,
  DELIVERABLE_TAB_INFORMATION,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT
} from 'utils/deliverables';
import { expandDataProps } from 'utils/helper/common';

import type {
  Data,
  DataStructureComponent,
  Deliverable,
  DeliverableLink,
  Subcampaign
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('../deliverable-edit-common/deliverable-edit-common.i18n.json'));

type Props = {
  offerId: number,
  deliverable: Deliverable,
  availableChannels: Array<string>,
  isExpanded?: boolean,
  subcampaign: Subcampaign,
  offerDeliverables: Array<Deliverable>,
  deliverablesForWMCReuse: Array<DataStructureComponent>,
  hideInfoNote: boolean,
  data?: Data,
  isV2?: boolean,
  onEdit: (a: Deliverable) => void,
  onDuplicate: (a: Deliverable) => void,
  onRemove: (a: Deliverable) => void,
  onToggleExpanded: (a: SyntheticEvent<HTMLElement>) => void
};

type State = {
  deliverable: Deliverable
};

function extendDeliverableWithFormData(initialDeliverable: Deliverable, formData: Deliverable): Deliverable {
  return {
    ...initialDeliverable,
    ...formData
  };
}

const CREATE_NEW_TASK_HASH = 'create-new-task-hash';
const COMMON_MSG_KEY = 'collabary_common';

const DivWithScrollTo = withScrollTo(() => <div />, {
  className: 'deliverable-edit__scroll-goal'
});

class DeliverableEdit extends React.PureComponent<Props, State> {
  static defaultProps = {
    isExpanded: false,
    isV2: false
  };

  constructor(props: Props) {
    super(props);

    const { deliverable } = props;
    const deliverableType = deliverable.deliverableType || DEFAULT_DELIVERABLE_TYPE_IN_COMPOSER;

    this.state = {
      deliverable: {
        ...deliverable,
        name: (deliverable && deliverable.name) || __t(`${COMMON_MSG_KEY}.types.${deliverableType}.singular`)
      }
    };

    if (this.props.isExpanded) {
      window.location.hash = CREATE_NEW_TASK_HASH;
    }
  }

  handleEdit = () => {
    const { onEdit, deliverable: originalDeliverable } = this.props;
    const {
      deliverable: stateDeliverable
    } = this.state;

    const newDeliverable = extendDeliverableWithFormData(originalDeliverable, stateDeliverable);

    onEdit(newDeliverable);
  };

  handleCommonUpdate = (deliverable: Partial<Deliverable>) => {
    const { isV2 } = this.props;

    const updatedDeliverable = deliverable;

    if (!isV2 && deliverable.landingPageUrl) {
      updatedDeliverable.trackingLinks = [{
        baseUrl: deliverable.landingPageUrl,
        description: deliverable.landingPageDescription
      }];
    }

    this.setState((state) => ({
      deliverable: {
        ...state.deliverable,
        ...updatedDeliverable
      }
    }), this.handleEdit);
  };

  handleLinksUpdate = (links: Array<DeliverableLink>) => {
    this.setState((state) => ({
      deliverable: {
        ...state.deliverable,
        links
      }
    }), this.handleEdit);
  };

  handleRemoveClick = () => {
    const { onRemove, deliverable: originalDeliverable } = this.props;
    const { deliverable: stateDeliverable } = this.state;

    const newDeliverable = extendDeliverableWithFormData(originalDeliverable, stateDeliverable);

    onRemove(newDeliverable);
  };

  handleDuplicateClick = () => {
    const { onDuplicate, deliverable: originalDeliverable } = this.props;
    const { deliverable: stateDeliverable } = this.state;

    const newDeliverable = extendDeliverableWithFormData(originalDeliverable, stateDeliverable);

    onDuplicate(newDeliverable);
  };

  renderCommonForm() {
    const { offerDeliverables, subcampaign, isV2 } = this.props;
    const { deliverable } = this.state;

    const Component = isV2 ? DeliverableEditCommonFormV2 : DeliverableEditCommonForm;

    return (
      <div className={`deliverable-edit__form-section${isV2 ? '' : '--v1'}`}>
        <Component
          campaignId={subcampaign.campaign.id}
          subcampaignId={subcampaign.id}
          creatorId={subcampaign.creatorProfile.id}
          deliverable={deliverable}
          availableChannels={this.props.availableChannels}
          offerDeliverables={offerDeliverables}
          onEdit={this.handleCommonUpdate}
        />
      </div>
    );
  }

  renderDetailsForm() {
    const { deliverable, isV2 } = this.props;
    const { deliverable: stateDeliverable } = this.state;
    const deliverableType = stateDeliverable.deliverableType;
    const isBlog = deliverable.deliverableType === 'blog';
    const numDeliverableLinks = isArray(stateDeliverable.links)
      ? stateDeliverable.links.length
      : 0;

    const Component = isV2 ? DeliverableDetailsFormV2 : DeliverableDetailsForm;

    return (!!INPUT_ROWS_TEMPLATES[deliverable.deliverableType]?.length || isBlog)
      && (
        <div className={`deliverable-edit__form-section${isV2 ? '' : '--v1'}`}>
          <Component
            key={`details-${deliverableType}-${numDeliverableLinks}`}
            deliverable={stateDeliverable}
            {...(isBlog) ? { isBlog } : null}
            onEdit={this.handleCommonUpdate}
          />
        </div>
      );
  }

  renderLinksForm() {
    const { isV2 } = this.props;
    const { deliverable } = this.state;

    return (
      <div className={`deliverable-edit__form-section${isV2 ? '' : '--v1'}`}>
        <DeliverableLinksForm
          key={`links-${deliverable.deliverableType}`}
          deliverable={deliverable}
          links={deliverable.links || []}
          onEdit={this.handleLinksUpdate}
          isV2={isV2}
        />
      </div>
    );
  }

  renderHeader() {
    const { offerId, subcampaign, isV2 } = this.props;
    const { deliverable } = this.state;

    const Component = isV2 ? DeliverableHeaderV2 : DeliverableHeader;

    return (
      <Component
        offerId={offerId}
        subcampaign={subcampaign}
        deliverable={deliverable}
        selectedTab={DELIVERABLE_TAB_INFORMATION}
        isDisabled={false}
        isForBrand
        hideTabs
      />
    );
  }

  renderActions() {
    const { isExpanded, onToggleExpanded } = this.props;
    const { deliverable } = this.state;

    return (
      <div className="deliverable-edit__action-buttons">
        <div className="deliverable-edit__action">
          <Tooltip
            message={t('subcampaign_offer.duplicate_deliverable_title')}
            width="short"
          >
            <SVGDynamicIcon
              height={20}
              width={20}
              name="duplicate-custom-fg"
              clickable
              onClick={this.handleDuplicateClick}
              customColor="grey"
              data={{
                arg: deliverable.id
              }}
            />
          </Tooltip>
        </div>
        <div className="deliverable-edit__action">
          <Tooltip
            message={t('subcampaign_offer.remove_deliverable_title')}
            width="short"
          >
            <SVGDynamicIcon
              height={20}
              width={20}
              name="trash-custom-fg"
              clickable
              onClick={this.handleRemoveClick}
              customColor="grey"
              data={{
                arg: deliverable.id
              }}
            />
          </Tooltip>
        </div>
        <div
          className="deliverable-edit__collapse-icon"
          onClick={onToggleExpanded}
          data-key={deliverable.id}
        >
          <SVGDynamicIcon
            name={`chevron-${isExpanded ? 'up' : 'down'}-custom-fg`}
            width={16}
            height={16}
          />
        </div>
      </div>
    );
  }

  render() {
    const { deliverable, data, hideInfoNote, isExpanded, isV2 } = this.props;
    const classes = classnames('deliverable-edit__body', `deliverable-edit__body${isV2 ? '--v2' : '--v1'}`, {
      'deliverable-edit__body--hidden': !isExpanded
    });

    const headerClasses = classnames('deliverable-edit__header', `deliverable-edit__header${isV2 ? '--v2' : '--v1'}`);

    return (
      <PageSection
        margin="none"
        padding="none"
        note={hideInfoNote ? null : <InfoNote>{t(`offer_form.add_new_${isV2 ? 'existing_' : ''}deliverable_info`)}</InfoNote>}
      >
        <div className="deliverable-edit">
          {
            isExpanded && (
              <DivWithScrollTo
                hash={isExpanded ? CREATE_NEW_TASK_HASH : undefined}
              />
            )
          }
          <div className={headerClasses}>
            {this.renderHeader()}
            {this.renderActions()}
          </div>
          <div
            className={classes}
            {...expandDataProps(data)}
          >
            {this.renderCommonForm()}
            {this.renderDetailsForm()}
            {
              deliverable.deliverableType !== DELIVERABLE_TYPE_ZALANDO_OUTFIT
                && this.renderLinksForm()
            }
          </div>
        </div>
      </PageSection>
    );
  }
}

export default DeliverableEdit;
